/**
 * PremiumPhoneButton — Bouton téléphone CONFILEX (authentifié + guest).
 *
 * Modes :
 *  - Authentifié  → POST /api/voice/call (rappel direct lié au compte)
 *  - Guest (anon) → POST /api/voice/guest/callback (intake public sécurisé)
 *
 * Champs guest :
 *  - prénom, téléphone, disponibilité, consentement RGPD, consentement enregistrement
 *  - Option "Être rappelé plus tard" → schedule_later=true
 */
import { useState } from "react";
import {
  Phone, X, Loader2, ShieldCheck, Volume2, AlertTriangle,
  Clock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AVAILABILITY_OPTIONS = [
  { value: "now", label: "Maintenant (dans les prochaines minutes)" },
  { value: "morning", label: "Ce matin (avant 12h)" },
  { value: "afternoon", label: "Cet après-midi (12h - 18h)" },
  { value: "evening", label: "Ce soir (18h - 20h)" },
  { value: "tomorrow", label: "Demain (créneau standard)" },
];

/**
 * Normalise un numéro français en E.164.
 * - "06 12 34 56 78" / "0612345678" → "+33612345678"
 * - "612345678"                     → "+33612345678"
 * - "+33 6 12 34 56 78"             → "+33612345678"
 * - "0033612345678"                 → "+33612345678"
 * - Numéro international déjà formaté (autre que FR) → conservé tel quel
 */
function normalizeFrenchPhone(raw) {
  if (!raw) return "";
  // Garder uniquement chiffres + le premier "+"
  let s = String(raw).trim().replace(/[\s\-.()]/g, "");
  if (s.startsWith("+")) return s; // déjà E.164
  // 00 33 6... → +336...
  if (s.startsWith("0033")) return "+" + s.slice(2);
  // 33 6... (sans +, sans 00) → +33 6...
  if (s.startsWith("33") && s.length >= 11) return "+" + s;
  // 0 6 12 34 56 78 → +33 6 12 34 56 78 (drop leading 0)
  if (s.startsWith("0") && s.length === 10) return "+33" + s.slice(1);
  // 6 12 34 56 78 (9 chiffres directs, FR sans 0 ni +33) → +33 6...
  if (/^[1-9]\d{8}$/.test(s)) return "+33" + s;
  // Tout autre format inconnu → renvoyer brut pour validation côté serveur
  return s;
}

export default function PremiumPhoneButton({
  missionId = null,
  expertId = null,
  variant = "pill",
  label = "Être rappelé par notre assistant",
  className = "",
  externalOpen = null,
  onExternalClose = null,
}) {
  const { user, loading: authLoading } = useAuth();
  const isGuest = !authLoading && !user;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== null ? externalOpen : internalOpen;
  const setOpen = externalOpen !== null
    ? (v) => { if (!v && onExternalClose) onExternalClose(); }
    : setInternalOpen;

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [availability, setAvailability] = useState("now");
  const [consentRgpd, setConsentRgpd] = useState(false);
  const [consentRecording, setConsentRecording] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [callState, setCallState] = useState(null); // null | 'queued' | 'scheduled' | 'fallback_sms' | 'mocked' | 'error'
  const [callMessage, setCallMessage] = useState("");
  const [resumeToken, setResumeToken] = useState(null);
  const [error, setError] = useState(null);

  const validate = () => {
    if (isGuest) {
      if (!firstName.trim()) return "Veuillez indiquer votre prénom.";
      if (!consentRgpd) return "Votre consentement RGPD est requis.";
    }
    const normalized = normalizeFrenchPhone(phone);
    if (!normalized.startsWith("+") || normalized.replace(/\D/g, "").length < 9) {
      return "Numéro invalide. Exemple : 06 12 34 56 78 ou +33 6 12 34 56 78.";
    }
    return null;
  };

  const submit = async (scheduleLater = false) => {
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    setError(null);

    const phoneE164 = normalizeFrenchPhone(phone);

    try {
      let res;
      if (isGuest) {
        res = await apiClient.post("/voice/guest/callback", {
          first_name: firstName.trim(),
          phone: phoneE164,
          availability,
          consent_rgpd: consentRgpd,
          consent_recording: consentRecording,
          schedule_later: scheduleLater,
          locale: "fr-FR",
        });
      } else {
        // Authenticated flow — endpoint inchangé
        res = await apiClient.post("/voice/call", {
          to_phone: phoneE164,
          mission_id: missionId,
          purpose: expertId ? "expert_intro" : "dossier_structuring",
          ambient: true,
        });
      }
      const d = res.data;

      // --- Mapping état + message utilisateur ---
      if (isGuest) {
        // Guest response shape
        setCallMessage(d.message || "");
        if (d.resume_token) setResumeToken(d.resume_token);
        if (d.mode === "live") {
          setCallState("queued");
          toast.success("Notre assistant vous appelle dans quelques minutes.");
        } else if (d.mode === "scheduled") {
          setCallState("scheduled");
          toast.success("Votre demande de rappel est enregistrée.");
        } else if (d.mode === "fallback_sms") {
          setCallState("fallback_sms");
          toast.info("Demande enregistrée — un SMS de confirmation a été envoyé.");
        } else {
          setCallState("mocked");
          toast.info("Service en activation — votre demande est bien enregistrée.");
        }
      } else if (d.ok && !d.mocked) {
        setCallState("queued");
        setCallMessage("Notre assistant vous appelle dans quelques minutes. Décrochez quand votre téléphone sonnera.");
        toast.success("Notre assistant vous appelle dans quelques instants.");
      } else if (d.mocked) {
        setCallState("mocked");
        setCallMessage("Le service vocal est en cours d'activation. Votre demande est enregistrée et un membre de l'équipe vous contactera très prochainement.");
        toast.info("Service vocal en cours d'activation — simulation enregistrée.");
      } else if (d.twilio_error_code === "21219") {
        setCallState("error");
        setError(
          "Votre numéro n'est pas encore vérifié sur notre service. Notre équipe vous contactera très prochainement.",
        );
      } else {
        setCallState("error");
        setError(d.twilio_message || "Le lancement a échoué. Veuillez réessayer plus tard.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const errMsg = formatApiError(detail) || "Impossible de lancer l'appel pour le moment.";
      setCallState("error");
      setError(errMsg);
      // Log explicite pour debug
      // eslint-disable-next-line no-console
      console.error("[CONFILEX VoiceCall] submit failed:", e?.response?.status, detail);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setError(null);
    setCallState(null);
    setCallMessage("");
    setPhone("");
    setFirstName("");
    setConsentRgpd(false);
    setConsentRecording(true);
    setAvailability("now");
    setResumeToken(null);
  };

  // ---------- Trigger ----------
  const triggerCommon = `inline-flex items-center gap-2 transition group ${className}`;
  const trigger =
    variant === "icon" ? (
      <button
        type="button"
        data-testid="premium-phone-trigger"
        onClick={() => setOpen(true)}
        aria-label={label}
        className={`${triggerCommon} w-10 h-10 rounded-full bg-[#031B12] text-[#F5C400] hover:bg-[#063D25] hover:shadow-lg hover:shadow-[#F5C400]/15`}
      >
        <Phone className="w-4 h-4" />
      </button>
    ) : variant === "full" ? (
      <button
        type="button"
        data-testid="premium-phone-trigger"
        onClick={() => setOpen(true)}
        className={`${triggerCommon} w-full justify-center px-5 py-3 rounded-lg bg-[#031B12] text-white hover:brightness-110 text-sm`}
      >
        <Phone className="w-4 h-4 text-[#F5C400]" /> {label}
      </button>
    ) : (
      <button
        type="button"
        data-testid="premium-phone-trigger"
        onClick={() => setOpen(true)}
        className={`${triggerCommon} px-4 py-2 rounded-full border border-[#031B12]/15 bg-[#031B12] text-[#031B12] hover:border-[#F5C400]/40 hover:bg-[#F7F7F2] text-xs font-medium`}
      >
        <Phone className="w-3.5 h-3.5 text-[#F5C400]" /> {label}
      </button>
    );

  return (
    <>
      {externalOpen === null && trigger}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#031B12]/50 backdrop-blur-sm p-4"
          data-testid="premium-phone-modal"
          onClick={close}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-[#031B12] shadow-2xl border border-[#031B12]/10 overflow-hidden max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#031B12] to-[#063D25] text-white px-6 py-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#F5C400]/15 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#F5C400]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5C400]">Assistant vocal</p>
                    <h3 className="font-serif italic text-lg leading-tight">Être rappelé par CONFILEX</h3>
                  </div>
                </div>
                <button
                  onClick={close}
                  data-testid="premium-phone-close"
                  disabled={submitting}
                  className="text-white/60 hover:text-white transition"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                {isGuest
                  ? "Renseignez quelques informations et notre assistant vocal vous rappelle. Sans création de compte."
                  : "Notre assistant vocal vous rappellera dans les prochaines minutes pour structurer votre dossier."}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-5 bg-[#F7F7F2]/40">
              {callState === "queued" ? (
                <div className="text-center py-4" data-testid="premium-phone-success">
                  <div className="w-12 h-12 rounded-full bg-[#063D25]/60 mx-auto flex items-center justify-center mb-3">
                    <Phone className="w-5 h-5 text-[#2ECC71]" />
                  </div>
                  <h4 className="font-serif italic text-lg text-[#031B12] mb-2">Nous vous rappelons</h4>
                  <p className="text-sm text-[#BFC8C2]">{callMessage}</p>
                  {resumeToken && (
                    <p className="text-[10px] text-[#BFC8C2]/70 italic mt-3">
                      Référence : <span className="font-mono">{resumeToken.slice(0, 8)}…</span>
                    </p>
                  )}
                </div>
              ) : callState === "scheduled" ? (
                <div className="text-center py-4" data-testid="premium-phone-scheduled">
                  <div className="w-12 h-12 rounded-full bg-[#F5C400]/10 mx-auto flex items-center justify-center mb-3">
                    <Clock className="w-5 h-5 text-[#F5C400]" />
                  </div>
                  <h4 className="font-serif italic text-lg text-[#031B12] mb-2">Rappel programmé</h4>
                  <p className="text-sm text-[#BFC8C2]">{callMessage}</p>
                </div>
              ) : callState === "fallback_sms" ? (
                <div className="text-center py-4" data-testid="premium-phone-fallback-sms">
                  <div className="w-12 h-12 rounded-full bg-[#F5C400]/10 mx-auto flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-5 h-5 text-[#F5C400]" />
                  </div>
                  <h4 className="font-serif italic text-lg text-[#031B12] mb-2">Demande enregistrée</h4>
                  <p className="text-sm text-[#BFC8C2]">{callMessage}</p>
                </div>
              ) : callState === "mocked" ? (
                <div className="text-center py-4" data-testid="premium-phone-mocked">
                  <div className="w-12 h-12 rounded-full bg-[#F5C400]/10 mx-auto flex items-center justify-center mb-3">
                    <Volume2 className="w-5 h-5 text-[#F5C400]" />
                  </div>
                  <h4 className="font-serif italic text-lg text-[#031B12] mb-2">Service en activation</h4>
                  <p className="text-sm text-[#BFC8C2]">{callMessage}</p>
                </div>
              ) : callState === "error" ? (
                <div className="py-2" data-testid="premium-phone-error">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F5C400]/10 border border-[#F5C400]/30 mb-3">
                    <AlertTriangle className="w-4 h-4 text-[#F5C400] mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800">{error}</p>
                  </div>
                  <button
                    onClick={() => { setCallState(null); setError(null); }}
                    className="text-xs text-[#031B12] underline"
                  >
                    Réessayer
                  </button>
                </div>
              ) : (
                <>
                  {isGuest && (
                    <div className="mb-4">
                      <label className="block text-xs uppercase tracking-wider text-[#BFC8C2]/85 mb-2">
                        Votre prénom
                      </label>
                      <input
                        type="text"
                        data-testid="premium-phone-firstname"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Marc"
                        disabled={submitting}
                        maxLength={80}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#063D25] bg-[#031B12] text-sm focus:outline-none focus:border-[#F5C400]"
                      />
                    </div>
                  )}

                  <label className="block text-xs uppercase tracking-wider text-[#BFC8C2]/85 mb-2">
                    Votre numéro de téléphone
                  </label>
                  <input
                    type="tel"
                    data-testid="premium-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="06 12 34 56 78"
                    disabled={submitting}
                    autoComplete="tel-national"
                    inputMode="tel"
                    className="w-full px-3 py-2.5 rounded-lg border border-[#063D25] bg-[#031B12] text-sm focus:outline-none focus:border-[#F5C400]"
                  />
                  {phone && (() => {
                    const normalized = normalizeFrenchPhone(phone);
                    const isValid = normalized.startsWith("+") && normalized.replace(/\D/g, "").length >= 9;
                    return isValid && normalized !== phone.replace(/\s/g, "") ? (
                      <p className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1.5" data-testid="premium-phone-normalized">
                        <CheckCircle2 className="w-3 h-3" />
                        Format international : <span className="font-mono font-medium">{normalized}</span>
                      </p>
                    ) : null;
                  })()}
                  <p className="text-[10px] text-[#BFC8C2]/70 italic mt-1.5">
                    France : 06… ou 09… — nous ajoutons automatiquement l'indicatif +33.
                  </p>

                  {isGuest && (
                    <>
                      <label className="block text-xs uppercase tracking-wider text-[#BFC8C2]/85 mb-2 mt-4">
                        Disponibilité souhaitée
                      </label>
                      <select
                        data-testid="premium-phone-availability"
                        value={availability}
                        onChange={(e) => setAvailability(e.target.value)}
                        disabled={submitting}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#063D25] bg-[#031B12] text-sm focus:outline-none focus:border-[#F5C400]"
                      >
                        {AVAILABILITY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>

                      <div className="mt-4 space-y-2.5">
                        <label className="flex items-start gap-2.5 text-xs cb-text-ivory cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="premium-phone-consent-rgpd"
                            checked={consentRgpd}
                            onChange={(e) => setConsentRgpd(e.target.checked)}
                            disabled={submitting}
                            className="mt-0.5 accent-[#F5C400]"
                          />
                          <span>
                            <span className="font-medium">J'accepte le traitement de mes données</span> pour ce rappel
                            (téléphone, prénom, disponibilité) conformément au <a href="/legal/rgpd" className="underline text-[#031B12]">RGPD</a>.
                          </span>
                        </label>
                        <label className="flex items-start gap-2.5 text-xs cb-text-ivory cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="premium-phone-consent-recording"
                            checked={consentRecording}
                            onChange={(e) => setConsentRecording(e.target.checked)}
                            disabled={submitting}
                            className="mt-0.5 accent-[#F5C400]"
                          />
                          <span>
                            J'accepte l'enregistrement vocal de l'appel pour produire une synthèse exploitable
                            (optionnel — révocable à tout moment).
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {error && (
                    <p className="text-xs text-red-400 mt-3 flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5" /> {error}
                    </p>
                  )}

                  {!isGuest && (
                    <ul className="mt-5 space-y-2 text-xs text-[#BFC8C2]">
                      {[
                        "Présentation calme et institutionnelle",
                        "Demande de consentement RGPD avant tout enregistrement",
                        "Structuration progressive de votre dossier",
                        "Synthèse exploitable transmise au professionnel compétent",
                      ].map((s) => (
                        <li key={s} className="flex items-start gap-2">
                          <ShieldCheck className="w-3.5 h-3.5 text-[#F5C400] mt-0.5 shrink-0" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    type="button"
                    data-testid="premium-phone-launch"
                    onClick={() => submit(false)}
                    disabled={submitting}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#031B12] text-white py-3 rounded-lg hover:brightness-110 disabled:opacity-50 text-sm"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Phone className="w-4 h-4 text-[#F5C400]" />
                        Me rappeler maintenant
                      </>
                    )}
                  </button>

                  {isGuest && (
                    <button
                      type="button"
                      data-testid="premium-phone-schedule"
                      onClick={() => submit(true)}
                      disabled={submitting}
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#031B12] border border-[#F5C400]/40 text-[#031B12] py-2.5 rounded-lg hover:border-[#F5C400] hover:bg-[#F5C400]/5 disabled:opacity-50 text-sm transition"
                    >
                      <Clock className="w-4 h-4 text-[#F5C400]" />
                      Être rappelé plus tard
                    </button>
                  )}

                  <p className="text-[10px] text-[#BFC8C2]/70 italic mt-3 text-center leading-relaxed">
                    Service institutionnel d'intermédiation. CONFILEX ne délivre pas directement
                    de conseil juridique, fiscal, comptable ou notarial.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
